import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import throttle from 'lodash.throttle';
import { AppContext } from '@prisjakt/ui-app-context';
import { getBrowserState } from '../helpers';
import { getAdContextCookie, updateAdContextCookie } from '../helpers/cookie';
import AdServiceContext from '../services/context';

updateAdContextCookie();

const adContextCookie = getAdContextCookie();

export const AdContext = React.createContext();
AdContext.displayName = 'AdContext';

const AdContextContainerPreConnected = ({ children, ads }) => {
  const [browserState, setBrowserState] = React.useState(getBrowserState());
  const context = React.useContext(AppContext);

  React.useEffect(() => {
    const resizeHandler = throttle(
      () => setBrowserState(getBrowserState()),
      200
    );
    if (IS_BROWSER) {
      window.addEventListener('resize', resizeHandler);
      window.addEventListener('orientationchange', resizeHandler);
    }
    // Cleanup
    return () => {
      if (IS_BROWSER) {
        window.removeEventListener('resize', resizeHandler);
        window.removeEventListener('orientationchange', resizeHandler);
      }
    };
  }, []);

  const adContext = React.useMemo(
    () => ({
      active: context.market.ads.active,
      market: context.market.code,
      state: {},
      actions: {
        log: (...args) => {
          if (adContextCookie.debug) {
            args.unshift('[AdContext]');
            console.log.apply(null, args); // eslint-disable-line no-console
          }
        },
      },
    }),
    [context.market.ads.active, context.market.code]
  );

  const value = React.useMemo(
    () => ({
      ...adContext,
      state: {
        ...browserState,
        ...ads,
      },
    }),
    [adContext, ads, browserState]
  );

  return <AdContext.Provider value={value}>{children}</AdContext.Provider>;
};

const { element, object } = PropTypes;

AdContextContainerPreConnected.propTypes = {
  children: element.isRequired,
  ads: object.isRequired,
};

export const AdContextContainer = connect(state => ({ ads: state.ads }))(
  AdContextContainerPreConnected
);

AdContextContainer.displayName = 'AdContextContainer';

export function withAds(Component) {
  const wrapper = (props, ref) => {
    return (
      <AdContext.Consumer>
        {adContext => (
          <AdServiceContext.Consumer>
            {adServiceContext => (
              <Component
                {...props}
                ref={ref}
                adContext={adContext}
                adServiceContext={adServiceContext}
              />
            )}
          </AdServiceContext.Consumer>
        )}
      </AdContext.Consumer>
    );
  };

  return React.forwardRef(wrapper);
}
